import { useQuery } from '@tanstack/react-query'
import {
  calendarDateInTimezone,
  computeInvoicingReadiness,
  resolveOrgTimezone,
} from '@trellis/shared'
import type {
  InvoicingReadinessDocumentType,
  InvoicingReadinessField,
} from '@trellis/shared'

import { getBrandingPreview, getOrganization } from '@/requests/organization'
import type { BrandingPreviewType } from '@/requests/organization'

export function useOrganization() {
  return useQuery({
    queryKey: ['organization'],
    queryFn: getOrganization,
    // Fetched once per session — invalidated by any org mutation.
    staleTime: Infinity,
  })
}

/**
 * Today's `yyyy-MM-dd` date in the organisation's timezone, for pre-filling date pickers.
 * Falls back to `DEFAULT_ORG_TIMEZONE` while the organisation is loading or unconfirmed.
 */
export function useOrganizationToday(): string {
  const { data: organizationResult } = useOrganization()
  return calendarDateInTimezone(
    new Date(),
    resolveOrgTimezone(organizationResult?.data?.timezone),
  )
}

export interface OrganizationReadiness {
  isReady: boolean
  missingFields: InvoicingReadinessField[]
  isLoading: boolean
}

/**
 * Client-side mirror of the backend's invoicing readiness gate (see
 * `OrganizationReadinessService.assertReadiness` in platform-api), so Send/Generate
 * PDF/Convert actions can be disabled up front instead of only failing with a 422 on
 * submit. Optimistic (`isReady: true`) while the organization is still loading — the
 * backend remains the source of truth either way.
 */
export function useOrganizationReadiness(
  documentType: InvoicingReadinessDocumentType,
): OrganizationReadiness {
  const { data: organizationResult, isLoading } = useOrganization()
  const organization = organizationResult?.data ?? null
  const { isReady, missingFields } = computeInvoicingReadiness(organization, documentType)

  return { isReady: isLoading ? true : isReady, missingFields, isLoading }
}

export function useBrandingPreviewQuery(
  type: BrandingPreviewType,
  pendingLogo: File | null,
  pendingLogoDelete: boolean,
  enabled = true,
) {
  /*
   * File objects aren't stable across renders — fingerprint by name/size/lastModified
   * so the query key only changes when the underlying file actually changes.
   */
  const logoFingerprint = pendingLogo
    ? `${pendingLogo.name}-${pendingLogo.size}-${pendingLogo.lastModified}`
    : null

  return useQuery({
    queryKey: [
      'organization',
      'branding-preview',
      type,
      logoFingerprint,
      pendingLogoDelete,
    ],
    queryFn: () =>
      getBrandingPreview(type, { logo: pendingLogo, removeLogo: pendingLogoDelete }),
    enabled,
  })
}
